/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useObservable } from '@squiz/dxp-console-support';
import { useMemo } from 'react';
import { Observable } from 'rxjs';
import { JsonObject } from 'type-fest';
import { ComponentServiceGetComponentDto } from '../../Component.service.dto';
import { EntityComponentVersions } from '../../Entities/entities';
import { getComponentService } from '../../Utilities/GetComponentService/GetComponentService';

/**
 * The result expected of the useComponentVersions hook.
 * @export
 */
export type ComponentVersionsResult = [EntityComponentVersions | null, JsonObject | null];

/**
 * React hook to get component versions.
 * @export
 * @param {ComponentServiceGetComponentDto} dto The details for the components to get.
 * @returns {ComponentVersionsResult} The components, or null if no value is available.
 */
export function useComponentVersions(dto: ComponentServiceGetComponentDto): ComponentVersionsResult {
  const componentService = getComponentService();

  const componentVersions$ = useMemo((): Observable<EntityComponentVersions | null> => {
    return componentService.getComponentVersions(dto);
  }, [componentService, dto]);

  const [componentVersions, error] = useObservable(componentVersions$, [componentService]);

  return useMemo((): ComponentVersionsResult => {
    return [componentVersions as EntityComponentVersions, error as JsonObject];
  }, [componentVersions, error]);
}
